import { CalendarOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Button, Select, Tooltip } from 'antd';
import cn from 'classnames';

import { AddWorkScheduleModal } from '../../AddWorkScheduleModal';
import { useExecutorSelect } from '../hooks';

import type { ExecutorSelectModel } from '../hooks';
import type { Moment } from 'moment';
import type { FC } from 'react';

import '../styles/ExecutorSelect.scss';

export interface ExecutorSelectBaseProps {
  model: ExecutorSelectModel;
  selectedDateMoment?: Moment;
  refreshCalendarData?: () => void;
}

export const ExecutorSelectBase: FC<ExecutorSelectBaseProps> = (props) => {
  const { model, selectedDateMoment, refreshCalendarData } = props;
  const {
    visibleEmployees,
    isDisabled,
    isDropdownOpen,
    isEmployeeSelectable,
    canOpenIndividualCalendar,
    selectedExecutor,
    workScheduleModal,
    canCreateWorkSchedule,
    handleSearch,
    handleDropdownOpenChange,
    handleOpenIndividualCalendar,
    handleOpenAddWorkSchedule,
    handleCloseWorkScheduleModal,
    handleSelectEmployee,
  } = model;

  return (
    <>
      <Select
        className="ExecutorSelect"
        placeholder="Выбрать исполнителя"
        showSearch
        filterOption={false}
        open={isDropdownOpen}
        value={selectedExecutor?.label ?? undefined}
        disabled={isDisabled}
        onSearch={handleSearch}
        onOpenChange={handleDropdownOpenChange}
        onFocus={() => handleDropdownOpenChange(true)}
        data-testid="calendar-executor-select"
        popupRender={() => (
          <div
            className="ExecutorSelect__dropdown small-scrollbar"
            data-testid="executor-dropdown"
            onMouseDown={(event) => {
              event.preventDefault();
            }}
          >
            {visibleEmployees.map(({ employeeId, label }) => (
              <div
                key={employeeId}
                className={cn('ExecutorSelect__item', {
                  'ExecutorSelect__item--clickable': isEmployeeSelectable,
                })}
                data-testid="executor-item"
                onClick={
                  isEmployeeSelectable
                    ? () => handleSelectEmployee(employeeId, label)
                    : undefined
                }
              >
                <span
                  className="ExecutorSelect__item-name"
                  data-testid="executor-name"
                >
                  {label}
                </span>
                <div
                  className="ExecutorSelect__item-actions"
                  onClick={(event) => event.stopPropagation()}
                >
                  {canOpenIndividualCalendar && (
                    <Tooltip title="Открыть индивидуальный ЖЗ" zIndex={1100}>
                      <Button
                        className="ExecutorSelect__action-button"
                        icon={<CalendarOutlined />}
                        data-testid="open-individual-calendar"
                        onClick={() => handleOpenIndividualCalendar(employeeId)}
                      />
                    </Tooltip>
                  )}
                  {canCreateWorkSchedule && (
                    <Tooltip
                      title="Добавить рабочий день в расписание"
                      zIndex={1100}
                    >
                      <Button
                        className="ExecutorSelect__action-button"
                        icon={<PlusCircleOutlined />}
                        data-testid="add-workday"
                        onClick={() =>
                          handleOpenAddWorkSchedule(employeeId, label)
                        }
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      />

      {workScheduleModal.isOpen && (
        <AddWorkScheduleModal
          onClose={handleCloseWorkScheduleModal}
          selectedDateMoment={selectedDateMoment}
          refreshCalendarData={refreshCalendarData}
          {...workScheduleModal}
        />
      )}
    </>
  );
};

export interface ExecutorSelectProps {}

export const ExecutorSelect: FC<ExecutorSelectProps> = () => {
  const model = useExecutorSelect();

  return <ExecutorSelectBase model={model} />;
};
